import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styles from './signIn.module.scss';
import trackHeapEvent from '../../../services/helpers/trackHeapEvent';
import DropdownClose from '../images/dropdown-close.svg';
import DropdownOpen from '../images/dropdown-open.svg';

const DROPDOWN_TOP_OFFSET = 10;
const DROPDOWN_RIGHT_OFFSET = 25;
const VIEWPORT_PADDING = 20;

export default function SignIn({
  signInData = {},
  isSubMenuOpen = null,
  onDropdownStateChange = null,
}) {
  const dropdownRef = useRef(null);
  const subDropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const containerRef = useRef(null);
  const [position, setPosition] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [isSubActive, setIsSubActive] = useState(false);

  const navActiveClass = isActive ? styles.active : styles.inActive;
  const buttonActiveClass = isSubActive ? styles.open : '';
  const subNavActiveClass = isSubActive ? styles.activeSub : styles.inActiveSub;

  useEffect(() => {
    if (isSubMenuOpen !== null) {
      setIsActive(false);
      setIsSubActive(false);
    }
  }, [isSubMenuOpen, setIsActive, setIsSubActive]);

  useEffect(() => {
    if (onDropdownStateChange) {
      onDropdownStateChange(isActive);
    }
  }, [isActive, onDropdownStateChange]);

  useEffect(() => {
    if (!isActive || !buttonRef.current) {
      setPosition(null);
      return undefined;
    }

    const updatePosition = () => {
      if (!dropdownRef.current || !buttonRef.current) return;

      const triggerRect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = dropdownRef.current.offsetWidth;
      const headerEl = buttonRef.current.closest('header');
      const top = headerEl
        ? headerEl.getBoundingClientRect().bottom + DROPDOWN_TOP_OFFSET
        : triggerRect.bottom + DROPDOWN_TOP_OFFSET;

      // Position dropdown so its right edge is 25px right of button's right edge
      // dropdownRight = buttonRight + 25
      // dropdownLeft = dropdownRight - dropdownWidth
      let left = triggerRect.right + DROPDOWN_RIGHT_OFFSET - dropdownWidth;

      // Ensure dropdown stays within viewport
      const maxLeft = window.innerWidth - dropdownWidth - VIEWPORT_PADDING;
      left = Math.max(VIEWPORT_PADDING, Math.min(left, maxLeft));

      // Calculate bridge offset - where the button is relative to dropdown
      const buttonCenter = triggerRect.left + triggerRect.width / 2;
      const dropdownLeft = left;
      const bridgeOffset = buttonCenter - dropdownLeft;

      setPosition({ left, top, bridgeOffset });
    };

    const frame = requestAnimationFrame(updatePosition);
    window.addEventListener('resize', updatePosition);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isActive]);

  const signInHeapTrack = async (heapEventKey) => {
    try {
      return await trackHeapEvent(
        { Name: 'Global Nav Sign In', CTA: heapEventKey.replace(/ /g, '_') },
        'CMS_Unification'
      );
    } catch (err) {
      return Promise.reject(err);
    }
  };

  const onClick = (heapKey) => {
    signInHeapTrack(heapKey);
    if (signInData.link) {
      window.location.href = signInData.link;
    } else if (isActive) {
      setIsActive(false);
      setIsSubActive(false);
    } else {
      setIsActive(true);
      setIsSubActive(false);
    }
  };

  const onSubClick = (e, heapKey) => {
    e.preventDefault();
    e.stopPropagation();
    setIsSubActive((prev) => !prev);
    signInHeapTrack(heapKey);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setIsActive(false);
      setIsSubActive(false);
    }
  };

  useEffect(() => {
    if (!isActive) return undefined;

    const handleClickOutside = (event) => {
      if (buttonRef.current && buttonRef.current.contains(event.target)) {
        return;
      }

      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsActive(false);
        setIsSubActive(false);
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 150);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isActive]);

  return (
    <div ref={containerRef} className={styles.signInContainer}>
      <div className={styles.signInWrapper}>
        {signInData?.title && (
          <button
            ref={buttonRef}
            id="signin-menu-button"
            type="button"
            className={styles.signInButton}
            onClick={(e) => {
              e.stopPropagation();
              onClick(signInData.title);
            }}
            aria-haspopup="true"
            aria-expanded={isActive}
            aria-controls="signin-menu"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick(signInData.title);
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                setIsActive(false);
                setIsSubActive(false);
              }
            }}
          >
            {signInData.title}
          </button>
        )}
      </div>
      {isActive && (
        <nav
          id="signin-menu"
          ref={dropdownRef}
          className={`${styles.menus} ${navActiveClass}`}
          style={
            position
              ? {
                  position: 'fixed',
                  left: `${position.left}px`,
                  top: `${position.top}px`,
                  '--bridge-offset': `${position.bridgeOffset}px`,
                }
              : {}
          }
          aria-labelledby="signin-menu-button"
        >
          <div
            role="menu"
            onKeyDown={handleKeyDown}
            onBlur={(e) => {
              if (
                !e.currentTarget.contains(e.relatedTarget) &&
                e.relatedTarget !== buttonRef.current
              ) {
                setIsActive(false);
                setIsSubActive(false);
              }
            }}
            tabIndex={-1}
          >
            <ul className={styles.navDropdown}>
              {signInData?.items &&
                signInData.items?.map((links, index) => {
                  const key = `sign-${index}`;
                  if (!links?.items) {
                    return (
                      <li className={styles.navDropdownItem} key={key}>
                        {links?.label && (
                          <a
                            href={links.link}
                            data-testid={links.label}
                            className={styles.link}
                            onClick={() =>
                              onClick(links?.heapEventKey || links.label)
                            }
                          >
                            {links.label}
                          </a>
                        )}
                      </li>
                    );
                  }
                  if (links?.items) {
                    return (
                      <li
                        key={key}
                        className={`${styles.navDropdownItem} ${styles.hasSub}`}
                      >
                        {links?.label && (
                          <button
                            id="signin-submenu"
                            type="button"
                            data-testid={links.label}
                            className={`${styles.link} ${buttonActiveClass}`}
                            onClick={(e) => onSubClick(e, links.label)}
                            aria-haspopup="true"
                            aria-expanded={isSubActive}
                          >
                            {links.label}
                            <img
                              src={
                                isSubActive
                                  ? DropdownOpen.src || DropdownOpen
                                  : DropdownClose.src || DropdownClose
                              }
                              alt=""
                              className={styles.dropdownIcon}
                            />
                          </button>
                        )}

                        <nav
                          ref={subDropdownRef}
                          className={`${styles.menuSub} ${subNavActiveClass}`}
                          aria-labelledby="signin-submenu"
                          onBlur={(e) => {
                            if (
                              !e.currentTarget.contains(e.relatedTarget) &&
                              e.relatedTarget !== buttonRef.current
                            ) {
                              setIsSubActive(false);
                            }
                          }}
                        >
                          <ul className={styles.navDropdownSub}>
                            {links.items.map((sublinks, i) => {
                              const subKey = `investor-${i}`;
                              return (
                                <li key={subKey}>
                                  <a
                                    href={sublinks.link}
                                    data-testid={sublinks.label}
                                    onClick={() => {
                                      signInHeapTrack(sublinks.label);
                                      setIsActive(false);
                                      setIsSubActive(false);
                                    }}
                                  >
                                    {sublinks.label}
                                  </a>
                                </li>
                              );
                            })}
                          </ul>
                        </nav>
                      </li>
                    );
                  }
                  return null;
                })}
            </ul>
          </div>
        </nav>
      )}
    </div>
  );
}

SignIn.propTypes = {
  signInData: PropTypes.object,
  isSubMenuOpen: PropTypes.bool,
  onDropdownStateChange: PropTypes.func,
};
